
import {
  computed,
  defineComponent,
  nextTick,
  onMounted,
  onUnmounted,
  ref,
} from "vue";
import { useChatConnection } from "@/composables/chatConnection";
import { useEventBus, useOnline } from "@vueuse/core";
import {
  MessageListScrollNotifyBus,
  MessageListScrollNotifyBusKey,
} from "@/types/MessageListScrollNotifyBus";
import { createPopup } from "@picmo/popup-picker";
import { PopupPickerController } from "@picmo/popup-picker/dist/popupPicker";
import { EmojiHappyIcon, PaperClipIcon } from "@heroicons/vue/outline";
import { stripHtml } from "string-strip-html";
import { canUseLocalstorage } from "@/util/localstorage";
import { IndexedDbStoreFactory, InMemoryStoreFactory } from "picmo";
import { useFileUploader } from "@/composables/fileUploader";
import { useApp } from "@/composables/use-app";
import { css } from "@emotion/css";
import { useWidgetTheme } from "@/composables/widgetTheme";
import { TinyColor, mostReadable } from "@ctrl/tinycolor";
import { useI18n } from "vue-i18n";

export default defineComponent({
  name: "MessengerEditor",
  components: {
    EmojiHappyIcon,
    PaperClipIcon,
  },
  setup() {
    const { status, sendReply: dispatchMessage } = useChatConnection();
    const { isPreviewMode } = useApp();
    const isOnline = useOnline();
    const scrollNotifier = useEventBus<MessageListScrollNotifyBus>(
      MessageListScrollNotifyBusKey
    );
    const useLocalstorage = canUseLocalstorage();
    const { theme } = useWidgetTheme();
    const { t } = useI18n();
    const { uploadFiles: doUploadFiles } = useFileUploader(t);

    const textareaWrapper = ref<HTMLDivElement>();
    const textArea = ref<HTMLTextAreaElement>();
    const emojiPickerButton = ref<HTMLElement>();
    const fileInput = ref<HTMLInputElement>();
    const text = ref<string>();
    /* eslint-disable prefer-const */
    let picker: PopupPickerController | undefined;

    const triggerFileInput = () => {
      fileInput.value?.click();
    };

    const uploadFile = (e: InputEvent) => {
      e.preventDefault();
      const target = e.currentTarget as HTMLInputElement;

      if (target.files?.length) {
        doUploadFiles(Array.from(target.files));
      }
    };

    onMounted(() => {
      const trigger = emojiPickerButton.value;

      if (!trigger) {
        console.warn("Emoji trigger was not present");
        return;
      }

      // Create the picker
      picker = createPopup(
        {
          emojisPerRow: 6,
          visibleRows: 5,
          className: "my-picker",
          animate: true,
          showPreview: false,
          showVariants: false,
          dataStore: useLocalstorage
            ? IndexedDbStoreFactory
            : InMemoryStoreFactory,
        },
        {
          referenceElement: trigger,
          triggerElement: trigger,
          position: "bottom-start",
        }
      );

      picker.addEventListener("emoji:select", (event: any) => {
        if (text.value) {
          text.value += event.emoji + " ";
        } else {
          text.value = event.emoji + " ";
        }

        textArea.value?.focus();
      });
    });

    onUnmounted(() => {
      picker?.destroy?.();
    });

    const toggleEmojiPicker = () => {
      picker?.toggle?.();
    };

    const replicateTextInput = () => {
      // Textarea input is replicated to textareaWrapper, a magic trick to ensure the textarea grows to the appropriate height
      if (!textareaWrapper.value) {
        return;
      }

      textareaWrapper.value.dataset.replicatedValue = text.value;
    };

    const canSendMessage = computed(() => {
      if (!text.value || text.value.trim() === "") {
        return false;
      }

      if (isPreviewMode.value) {
        return true;
      }

      if (!isOnline.value || status.value !== "OPEN") {
        return false;
      }

      return true;
    });

    const sendMessage = () => {
      if (!canSendMessage.value) {
        return;
      }

      const safeMessage = stripHtml(text.value!.trim()).result;

      try {
        dispatchMessage(safeMessage);
        text.value = "";
        replicateTextInput();

        nextTick(() => {
          scrollNotifier.emit();
        });
      } catch (err) {
        console.warn("There was an error sending the message");
      }
    };

    const backgroundClass = computed(
      () => css`
        background-color: ${theme.value?.mainBackgroundColor};
      `
    );

    const submitButtonClass = computed(() => {
      const colorInfo = new TinyColor(theme.value?.primaryColor);
      const hoverColor = colorInfo.darken(10);

      return css`
        color: ${theme.value?.primaryColor};

        &:hover {
          color: #${hoverColor.toHex()};
        }
      `;
    });

    const placeholderClass = computed(() => {
      const placeholderColor =
        mostReadable(theme.value?.mainBackgroundColor ?? "#FFFFFF", [
          "#a9a9a9",
          "#d1d1d1",
        ])?.toHexString() ?? "#a9a9a9";

      return css`
        ::placeholder {
          /* Chrome, Firefox, Opera, Safari 10.1+ */
          color: ${placeholderColor};
          opacity: 1; /* Firefox */
        }

        :-ms-input-placeholder {
          /* Internet Explorer 10-11 */
          color: ${placeholderColor};
        }

        ::-ms-input-placeholder {
          /* Microsoft Edge */
          color: ${placeholderColor};
        }
      `;
    });

    const emojiButtonClass = computed(() => {
      const colorInfo = new TinyColor(theme.value?.primaryColor);
      const hoverColor = colorInfo.darken(10);

      return css`
        color: ${theme.value?.primaryColor};

        &:hover {
          color: #${hoverColor.toHex()};
        }
      `;
    });

    const attachmentButtonClass = computed(() => {
      const colorInfo = new TinyColor(theme.value?.primaryColor);
      const hoverColor = colorInfo.darken(10);

      return css`
        color: ${theme.value?.primaryColor};

        &:hover {
          color: #${hoverColor.toHex()};
        }
      `;
    });

    return {
      textareaWrapper,
      emojiPickerButton,
      replicateTextInput,
      text,
      canSendMessage,
      sendMessage,
      toggleEmojiPicker,
      textArea,
      triggerFileInput,
      fileInput,
      uploadFile,

      isPreviewMode,
      backgroundClass,
      submitButtonClass,
      emojiButtonClass,
      placeholderClass,
      attachmentButtonClass,
      t,
    };
  },
});
